/**
 * Safety and Security Section Component
 * Displays safety and security information for the location
 */

import React from 'react';
import {
  typographyColors,
  spacing,
  typography,
  fontPrimary,
} from '@/design-system/utils';
import { SectionHeader } from '@/components/ui/section-header';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { IconItemList, IconItem } from './IconItemList';

export interface SafetySection {
  id: string;
  title: string;
  items: string[];
}

export interface SafetyAndSecuritySectionProps {
  title?: string;
  sections: SafetySection[];
}

// Safety Item List Component with Icons
const SafetyItemList: React.FC<{ items: string[] }> = ({ items }) => {
  if (!items || items.length === 0) {
    return null;
  }

  // Custom color function for Emergency Services
  const getCustomTextColor = (label: string): string | undefined => {
    const isEmergencyServices =
      label.toLowerCase().trim() === 'emergency services';
    return isEmergencyServices ? typographyColors.primary : undefined;
  };

  const getCustomIconColor = (label: string): string | undefined => {
    const isEmergencyServices =
      label.toLowerCase().trim() === 'emergency services';
    return isEmergencyServices ? typographyColors.primary : undefined;
  };

  // Map string items to IconItem format; pass id derived from label so each item can resolve to a distinct icon
  const iconItems: IconItem[] = items.map((item, index) => ({
    label: item,
    id: item
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, ''),
    key: `${item}-${index}`,
  }));

  return (
    <IconItemList
      items={iconItems}
      iconSize={20}
      iconType='activity'
      textColor={typographyColors.text}
      lineHeight='relaxed'
      getCustomTextColor={getCustomTextColor}
      getCustomIconColor={getCustomIconColor}
      mobileColumns={1}
      desktopColumns={1}
      rowGap={spacing[4]} // 16px vertical gap between safety items
      columnGap={spacing[6]}
    />
  );
};

export const SafetyAndSecuritySection: React.FC<
  SafetyAndSecuritySectionProps
> = ({ title = 'Safety & Security', sections }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  return (
    <div>
      {/* Section Header */}
      <SectionHeader title={title} />

      {/* Safety Sections Container */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? 0 : spacing[4],
          padding: `0 ${spacing[3]}px ${spacing[3]}px`,
          marginTop: spacing[3],
        }}
      >
        {/* First Row - Two sections side by side on desktop, stacked on mobile */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: `${spacing[4]}px ${spacing[6]}px`, // 16px vertical gap, 24px horizontal gap between columns
          }}
        >
          {sections.slice(0, 2).map(section => (
            <div key={section.id}>
              <h4
                style={{
                  fontFamily: fontPrimary,
                  fontSize: typography.size.base,
                  fontWeight: typography.weight.semibold,
                  color: typographyColors.primary,
                  marginBottom: spacing[3],
                  lineHeight: typography.lineHeight.normal,
                }}
              >
                {section.title}
              </h4>
              <div style={{ marginBottom: spacing[4] }}>
                <SafetyItemList items={section.items} />
              </div>
            </div>
          ))}
        </div>

        {/* Second Row - Single section full width */}
        {sections.length > 2 && (
          <div>
            <h4
              style={{
                fontFamily: fontPrimary,
                fontSize: typography.size.base,
                fontWeight: typography.weight.semibold,
                color: typographyColors.primary,
                marginBottom: spacing[3],
                lineHeight: typography.lineHeight.normal,
              }}
            >
              {sections[2].title}
            </h4>
            <SafetyItemList items={sections[2].items} />
          </div>
        )}
      </div>
    </div>
  );
};
